import { Routes } from '@angular/router';
import { AuthCallbackComponent } from './components/auth-callback.component';
import { authGuard } from './guards/auth.guard';
import { authenticatedGuard } from './guards/authenticated.guard';
import { accessPausedGuard } from './guards/access-paused.guard';
import { portalGuard } from './guards/portal.guard';
import { publicPausedGuard } from './guards/public-paused.guard';
import { permissionGuard } from './guards/permission.guard';
// Share-mount route families source their guard values from the ONE roster the share
// arrival pre-check also reads — route guards and the honest-arrival interstitial agree
// by construction (never re-inline a permission literal on these six families).
import { SHARE_MOUNT_GUARDS } from './guards/share-mount-roster';
import { temporalGuard } from './guards/temporal.guard';
import { planFitGuard } from './guards/plan-fit.guard';
import { memberAreaMatch, publicModeOnlyMatch, toRefusalRoutes } from './guards/member-area.match';
import { RouteData } from './guards/route-data';
import { guardsRerunInsideRebuildWindow } from './route-rebuild';

/**
 * The member-only top-level nodes — every page a public brand's visitor may NOT enter. They mount under ONE
 * pathless parent whose match answers "not public mode" (awaiting the settled membership for a signed-in caller),
 * and the refusal twin derived from this same array renders the public refusal face at each of these paths, at
 * any depth, behind the inverse gate. So for a public-mode caller these routes do not exist: no member host is
 * constructed, none of their guards runs, no member chunk downloads, the URL stays. A member — and every caller of
 * a brand whose public access is off — meets them exactly as before. The nodes themselves are unchanged; a NEW
 * member-only page joins this array (the route roster Fact holds every guarded, unflagged node to it).
 */
export const MEMBER_ROUTES: Routes = [
  // Home — member's personalized dashboard (authGuard only — all members)
  {
    path: 'home',
    loadComponent: () => import('./pages/home/home.component').then(m => m.HomeComponent),
    canActivate: [authGuard, temporalGuard]
  },
  // ═══════════════════════════════════════════════════════════════
  // Content Modules (AccessDrive, AccessCollections) — the two public modules sit at the top level
  // ═══════════════════════════════════════════════════════════════
  {
    path: 'drive',
    loadChildren: () => import('./pages/drive/drive.routes').then(m => m.DRIVE_ROUTES),
    canActivate: [permissionGuard(SHARE_MOUNT_GUARDS['/drive'].permission)]
  },
  {
    path: 'collections',
    loadChildren: () => import('./pages/collections/collections.routes').then(m => m.COLLECTIONS_ROUTES),
    canActivate: [permissionGuard('ACCESS_COLLECTIONS'), temporalGuard]
  },
  // ═══════════════════════════════════════════════════════════════
  // Intelligence Modules
  // ═══════════════════════════════════════════════════════════════
  {
    path: 'analytics',
    loadComponent: () => import('./pages/analytics/analytics.component').then(m => m.AnalyticsComponent),
    canActivate: [permissionGuard('VIEW_ANALYTICS'), temporalGuard]
  },
  {
    path: 'insights',
    loadComponent: () => import('./pages/insights/insights.component').then(m => m.InsightsComponent),
    canActivate: [permissionGuard('VIEW_INSIGHTS'), temporalGuard],
    // The root and the report address render ONE page instance across a rail pick (the
    // route-reuse strategy keys on this group) — the page's paramMap subscription does the hop.
    data: { reuseGroup: 'insights' } satisfies RouteData
  },
  {
    // A report's own address — the bell for a completed report lands HERE (never on whichever
    // report happens to be newest), and the browser bar carries a link a member can copy. Same
    // two guards as the module root; the route spec holds every `insights*` path to that pair.
    path: 'insights/:id',
    loadComponent: () => import('./pages/insights/insights.component').then(m => m.InsightsComponent),
    canActivate: [permissionGuard('VIEW_INSIGHTS'), temporalGuard],
    data: { reuseGroup: 'insights' } satisfies RouteData
  },
  {
    path: 'compliance',
    loadComponent: () => import('./pages/compliance/compliance.component').then(m => m.ComplianceComponent),
    canActivate: [permissionGuard('VIEW_COMPLIANCE'), temporalGuard]
  },
  // ═══════════════════════════════════════════════════════════════
  // Workflow Modules
  // ═══════════════════════════════════════════════════════════════
  {
    path: 'hub',
    loadComponent: () => import('./pages/hub/hub.component').then(m => m.HubComponent),
    canActivate: [permissionGuard('VIEW_HUB'), temporalGuard]
  },
  {
    path: 'campaigns',
    loadComponent: () => import('./pages/campaigns/campaigns.component').then(m => m.CampaignsComponent),
    canActivate: [permissionGuard(SHARE_MOUNT_GUARDS['/campaigns'].permission), temporalGuard]
  },
  {
    path: 'campaigns/:slug',
    loadComponent: () => import('./pages/campaigns/campaign-workspace/campaign-workspace.component').then(m => m.CampaignWorkspaceComponent),
    canActivate: [permissionGuard(SHARE_MOUNT_GUARDS['/campaigns'].permission), temporalGuard]
  },
  {
    path: 'selects',
    loadComponent: () => import('./pages/selects/selects.component').then(m => m.SelectsComponent),
    canActivate: [permissionGuard(SHARE_MOUNT_GUARDS['/selects'].permission), temporalGuard]
  },
  {
    path: 'selects/:slug',
    loadComponent: () => import('./pages/selects/selection-workspace/selection-workspace.component').then(m => m.SelectionWorkspaceComponent),
    canActivate: [permissionGuard(SHARE_MOUNT_GUARDS['/selects'].permission), temporalGuard],
  },
  {
    path: 'reviews',
    loadComponent: () => import('./pages/reviews/reviews.component').then(m => m.ReviewsComponent),
    canActivate: [permissionGuard(SHARE_MOUNT_GUARDS['/reviews'].permission), temporalGuard]
  },
  {
    path: 'reviews/:slug',
    loadComponent: () => import('./pages/reviews/review-detail/review-detail.component').then(m => m.ReviewDetailComponent),
    canActivate: [permissionGuard(SHARE_MOUNT_GUARDS['/reviews'].permission), temporalGuard],
  },
  // ═══════════════════════════════════════════════════════════════
  // Utility routes (authGuard only)
  // ═══════════════════════════════════════════════════════════════
  {
    path: 'activity',
    loadComponent: () => import('./pages/activity/activity.component').then(m => m.ActivityComponent),
    canActivate: [authGuard, temporalGuard]
  },
  {
    // "Shares I sent" — the member's own outsider-share list (self-scoped; no admin gate).
    path: 'shares',
    loadComponent: () => import('./pages/shares-sent/shares-sent.component').then(m => m.SharesSentComponent),
    canActivate: [authGuard, temporalGuard]
  },
  {
    // Catch-up email landings: fetch the member's own bell row, mark it read, and navigate
    // where the bell click would have (one shared routing home). Deliberately NOT
    // temporal-guarded — the page itself exits "Time travel" before navigating.
    path: 'open-notification/:id',
    loadComponent: () => import('./pages/open-notification/open-notification.component').then(m => m.OpenNotificationComponent),
    canActivate: [authGuard]
  },
  // "Time travel" boundary interstitial — in-mode navigation to excluded surfaces lands here
  // (temporalGuard redirect, asOf preserved so the mode + banner persist). NEVER temporal-guarded
  // itself (no loop); authGuard only.
  {
    path: 'time-travel-boundary',
    loadComponent: () => import('./pages/temporal-boundary/temporal-boundary.component').then(m => m.TemporalBoundaryComponent),
    canActivate: [authGuard]
  },
  // Share-arrival interstitial — a shared destination the arrival pre-check refused to
  // navigate to (the member's role does not open the module, or the shared thing is in
  // Trash) lands here with the honest sentence, never the permission guard's generic
  // toast-and-bounce. authGuard only.
  {
    path: 'share-arrival',
    loadComponent: () => import('./pages/share-arrival/share-arrival.component').then(m => m.ShareArrivalComponent),
    canActivate: [authGuard]
  },
  // ═══════════════════════════════════════════════════════════════
  // Admin routes — each child route is gated individually by
  // permissionGuard(<specific>); no blanket PrivilegeLevel check
  // here. ONE guard over every child: while the brand holds more
  // people or languages than its plan includes, every admin page
  // opens on the fit step (the two billing pages excepted) — on
  // the children, so the refusal twin never carries it.
  // ═══════════════════════════════════════════════════════════════
  {
    path: 'admin',
    loadChildren: () => import('./pages/admin/admin.routes').then(m => m.ADMIN_ROUTES),
    canActivate: [temporalGuard],
    canActivateChild: [planFitGuard]
  },
];

export const routes: Routes = [
  // Portal — the brand portal, the public-facing landing for unauthenticated visitors.
  // Authenticated users are redirected to /home by portalGuard.
  {
    path: '',
    loadComponent: () => import('./pages/portal/portal.component').then(m => m.PortalComponent),
    // The paused faces' door runs AFTER the portal's own guard: a visitor on a brand whose public faces are paused is sent to
    // the paused page with the address they wanted; a member was already sent home.
    canActivate: [portalGuard, publicPausedGuard],
    pathMatch: 'full',
    // Public anonymous surface: no app sidebar, and NO silent SSO probe — an anonymous visitor must never be
    // redirected to the auth server (the probe caused a full round-trip + login_required + spinner hang).
    // Members with saved tokens are still auto-routed to /home (checkAuth + portalGuard, no probe needed);
    // a cookie-only member signs in explicitly (one-click SSO). The probe still runs for deep-linked PROTECTED
    // routes (e.g. /manual hit while logged out), where attempting auto-login is genuinely useful.
    // In public mode the portal sits INSIDE the shell (the sidebar shows): the ONE node the public shell lifts the chrome on.
    // With the public modules it is the brand's PUBLIC SURFACE, the landings the public analytics collector counts.
    data: { hideSidebar: true, shellInPublicMode: true, publicPortal: true } satisfies RouteData
  },
  // ═══════════════════════════════════════════════════════════════
  // The public modules (AccessManual, AccessMedia) and public search
  // ═══════════════════════════════════════════════════════════════
  // The two public modules: a public brand's visitor may enter them (the flag sits on the node itself and on
  // every node of the module's own table — the guards read it on their own snapshot). The three doors re-run
  // their guards inside the rebuild window: a public-mode transition under a public page — ENDING (the brand's
  // switch went off under a visitor, or the caller was admitted) or BEGINNING on a public-readable route (a
  // member's session or membership ended) — re-runs the same URL, and the router would keep a guard's last
  // answer on a same-URL navigation — the guard is where the caller meets the brand portal or the sign-in
  // door, or is admitted at the first seat, so it must run again (the route roster Fact holds the three to the
  // predicate; the window's reasons are its one home's, `route-rebuild.ts`).
  {
    path: 'manual',
    loadChildren: () => import('./pages/manual/manual.routes').then(m => m.MANUAL_ROUTES),
    canActivate: [permissionGuard(SHARE_MOUNT_GUARDS['/manual'].permission)],
    runGuardsAndResolvers: guardsRerunInsideRebuildWindow,
    data: { publicReadable: true } satisfies RouteData
  },
  {
    path: 'media',
    loadChildren: () => import('./pages/media/media.routes').then(m => m.MEDIA_ROUTES),
    canActivate: [permissionGuard(SHARE_MOUNT_GUARDS['/media'].permission)],
    runGuardsAndResolvers: guardsRerunInsideRebuildWindow,
    data: { publicReadable: true } satisfies RouteData
  },
  {
    path: 'search',
    loadComponent: () => import('./pages/search/search.component').then(m => m.SearchComponent),
    canActivate: [authGuard, temporalGuard],
    runGuardsAndResolvers: guardsRerunInsideRebuildWindow,
    // Search over the public content is a public door too.
    data: { publicReadable: true } satisfies RouteData
  },
  // ═══════════════════════════════════════════════════════════════
  // The member area and its refusal twin — the one seat of the public refusal face
  // ═══════════════════════════════════════════════════════════════
  // Every member-only node under ONE pathless parent: it matches when the caller is not in public mode (a
  // signed-in caller's membership settled first), so for a public brand's visitor these routes do not exist.
  {
    path: '',
    canMatch: [memberAreaMatch],
    children: MEMBER_ROUTES
  },
  // The twin, derived from the same array: the refusal face at every member path, at any depth, reachable in
  // public mode alone — a member's nested-unknown member URL still falls through to the wildcard below.
  {
    path: '',
    canMatch: [publicModeOnlyMatch],
    children: toRefusalRoutes(MEMBER_ROUTES)
  },
  // ═══════════════════════════════════════════════════════════════
  // Membership & Auth
  // ═══════════════════════════════════════════════════════════════
  {
    path: 'join',
    loadComponent: () => import('./pages/join/join.component').then(m => m.JoinComponent),
    // Handles its own auth flow → no sidebar, skip the silent SSO probe.
    data: { hideSidebar: true } satisfies RouteData
  },
  {
    path: 'request-access',
    loadComponent: () => import('./pages/request-access/request-access.component').then(m => m.RequestAccessComponent),
    canActivate: [authenticatedGuard],
    data: { hideSidebar: true } satisfies RouteData
  },
  // The visitor's paused page: the brand's public faces are paused (its plan ended more than the grace ago) — the fourth
  // single-card corridor page. No guard and no public-readable flag, like the share and portal routes: the page itself admits
  // only while the pause holds (a live brand lands on the public root), sends an admitted member onward, and offers Sign in
  // to an anonymous visitor alone, carrying the address they wanted.
  {
    path: 'paused',
    loadComponent: () => import('./pages/public-paused/public-paused.component').then(m => m.PublicPausedComponent),
    data: { hideSidebar: true } satisfies RouteData
  },
  // The honest wall for a member whose brand access is paused — by an admin, or by the brand's plan (the
  // fit paused them; the page reads the cause and speaks to it). Admission is owned
  // entirely by the guard (a component-side check would bounce a direct load/refresh —
  // standing is unloaded until the guard awaits it).
  {
    path: 'access-paused',
    loadComponent: () => import('./pages/access-paused/access-paused.component').then(m => m.AccessPausedComponent),
    canActivate: [authenticatedGuard, accessPausedGuard],
    data: { hideSidebar: true } satisfies RouteData
  },
  {
    path: 'auth/signin-oidc',
    component: AuthCallbackComponent,
    // Transient OIDC callback (AuthCallbackComponent owns checkAuth) — no sidebar.
    data: { hideSidebar: true } satisfies RouteData
  },
  // Anonymous public collection share page — NO guard (explicitly reachable logged-out AND logged-in;
  // must NOT carry portalGuard, which bounces authenticated users to /home). Above the '**' catch-all.
  {
    path: 'share/:token',
    loadComponent: () => import('./pages/share/share.component').then(m => m.ShareComponent),
    // Fully anonymous public page: no sidebar, skip the silent SSO probe (no login for a logged-out visitor).
    data: { hideSidebar: true } satisfies RouteData
  },
  // Published-portal collection drill-in — public self-serve presentation (indexable), for a visitor and a signed-in
  // member alike: members see the SAME public view (it is the public face), read with their sign-in, so portalGuard
  // must NOT bounce them; the paused faces' door below is the route's only guard. Above the '**' catch-all;
  // collision-free under the /portal/ namespace.
  {
    path: 'portal/:urlId',
    loadComponent: () => import('./pages/portal/portal-collection/portal-collection.component').then(m => m.PortalCollectionComponent),
    // The paused faces' door alone (members see the same public view, so no other guard): a visitor on a brand whose public
    // faces are paused is sent to the paused page with the address they wanted.
    canActivate: [publicPausedGuard],
    // The portal's second node: a landing the public analytics collector counts (the share and join pages are not).
    data: { hideSidebar: true, publicPortal: true } satisfies RouteData
  },
  // Fallback
  {
    path: '**',
    redirectTo: '/'
  }
];
